import {
  TypeAheadConfig,
  SetupBaseRequestInfo,
  RecordsSearchFilterRequest,
  FilterFieldValueType,
  RecordsSearchFilterResponse,
  PrimitiveValueType,
  RowDataReducerState,
  EvaluatedResults,
  SetupListNavInfoByDepartment,
  IAMUserState,
} from "../../../types";
import { DEFAULT_RecordsSearchFilterResponse } from "../constants";
import { getRecordsBySearchFilter } from "./dataHelpers";
import { getStoreOptionByGroupId } from "./group-id";
import { getResolvedDynamicRowDataValue } from "./rowDataHelpers";
import { getStoreLevelAccountName } from "./sharedHelpers";
import { getDepartmentIdByStore } from "./storeHelpers";

export const getTypeAheadRequest = ({
  propertyKey,
  typeaheadConfig,
  rowIndex,
  rowDataReducerState,
  setupBaseRequestInfo,
  existingStoreIdValue,
  existingGroupIdValue,
  iamUserState,
  setupListNavInfoByDepartment,
}: {
  propertyKey: string;
  typeaheadConfig: TypeAheadConfig;
  rowIndex: number | undefined;
  rowDataReducerState: RowDataReducerState;
  setupBaseRequestInfo: SetupBaseRequestInfo;
  existingStoreIdValue: string | undefined;
  existingGroupIdValue?: string;
  iamUserState: IAMUserState;
  setupListNavInfoByDepartment: SetupListNavInfoByDepartment;
}): RecordsSearchFilterRequest | undefined => {
  const { storeId, functionalArea } = setupBaseRequestInfo;
  const departmentId = getDepartmentIdByStore({
    store: storeId,
    department: functionalArea,
    extendedAccountInfo: iamUserState.extendedAccountInfo,
    setupListNavInfoByDepartment,
  });
  const fieldSpecificStoreId = getStoreIdForTypeAhead({
    storeId,
    iamUserState,
    existingStoreIdValue,
    existingGroupIdValue,
  });
  const accountName = getStoreLevelAccountName(
    fieldSpecificStoreId,
    functionalArea,
    iamUserState.extendedAccountInfo,
    setupListNavInfoByDepartment,
  );

  return {
    ...setupBaseRequestInfo,
    accountName,
    url: typeaheadConfig.url,
    httpMethod: typeaheadConfig.httpMethod,
    body: {
      ...(typeaheadConfig.body.searchKey
        ? { searchKey: typeaheadConfig.body.searchKey }
        : {}),
      ...(typeaheadConfig.body.searchValue
        ? { searchValue: typeaheadConfig.body.searchValue }
        : {}),
      filters: typeaheadConfig.body.filters
        ? typeaheadConfig.body.filters.map((filter) => ({
            ...filter,
            value: getResolvedDynamicRowDataValue({
              propertyKey,
              dynamicField: filter.value,
              rowData: rowDataReducerState.rowData,
              rowIndex,
            }),
          }))
        : [],
      responseFields: typeaheadConfig.body.responseFields,
      ...(typeaheadConfig.body.department ? { department: departmentId } : {}),
    },
    existingStoreIdValue: fieldSpecificStoreId,
    existingGroupIdValue: existingGroupIdValue,
    valueField: typeaheadConfig.valueField,
    labelField: typeaheadConfig.labelField,
    defaultIdentifier: typeaheadConfig.defaultIdentifier,
  };
};

export const getEvaluatedTypeAheadRequest = (
  evalutedResults: EvaluatedResults,
  typeAheadRequest: RecordsSearchFilterRequest,
): RecordsSearchFilterRequest => {
  const evaluateValue = (value: FilterFieldValueType): FilterFieldValueType => {
    if (typeof value === "string" && value.startsWith("#")) {
      const result = evalutedResults[value.slice(1)] as string;
      return result !== "" ? result : value;
    }
    return value;
  };

  return {
    ...typeAheadRequest,
    body: {
      ...typeAheadRequest.body,
      filters: typeAheadRequest.body.filters
        ? typeAheadRequest.body.filters.map((filter) => ({
            ...filter,
            value: evaluateValue(filter.value),
          }))
        : [],
    },
  };
};

export const getSearchValueUpdatedTypeAheadRequest = (
  typeAheadRequest: RecordsSearchFilterRequest,
  searchValue: string,
): RecordsSearchFilterRequest => {
  return {
    ...typeAheadRequest,
    body: {
      ...typeAheadRequest.body,
      searchValue,
    },
  };
};

export const getDynamicDependentFieldsAndValues = (
  typeaheadConfig: TypeAheadConfig | undefined,
  propertyKey: string,
  rowDataReducerState: RowDataReducerState,
): Record<string, PrimitiveValueType> => {
  const fieldValues: Record<string, PrimitiveValueType> = {};

  typeaheadConfig?.body.filters &&
    typeaheadConfig?.body.filters.forEach((filter) => {
      const { value } = filter;

      if (typeof value === "string" && value.startsWith("$")) {
        fieldValues[value] = getResolvedDynamicRowDataValue({
          propertyKey,
          dynamicField: value,
          rowData: rowDataReducerState.rowData,
        }) as PrimitiveValueType;
      }
    });

  return fieldValues;
};

export const fetchTypeAheadSearchResponse = async (
  typeAheadRequest: RecordsSearchFilterRequest,
  evalMethod: string,
): Promise<RecordsSearchFilterResponse> => {
  switch (evalMethod) {
    case "updateCOAGroupCodeDescription":
      return DEFAULT_RecordsSearchFilterResponse;
    default: {
      const { response } = await getRecordsBySearchFilter(typeAheadRequest);
      return response;
    }
  }
};

export const getStoreIdForTypeAhead = ({
  storeId,
  iamUserState,
  existingStoreIdValue,
  existingGroupIdValue,
}: {
  storeId: string;
  iamUserState: IAMUserState;
  existingStoreIdValue?: string;
  existingGroupIdValue?: string;
}): string => {
  if (existingStoreIdValue) return existingStoreIdValue;
  else if (existingGroupIdValue)
    return getStoreOptionByGroupId({
      selectedGroupId: existingGroupIdValue,
      iamUserState,
    }).value;
  return storeId;
};
